import { Card, CardBody, CardHead } from "@/components/admin/card";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useAnomalies, useAuditLog } from "@/lib/finance/finance-hooks";
import type { TagClass } from "@/lib/status";

const ACTION_CLASS: Record<string, TagClass> = {
	PERIOD_LOCK: "info",
	PERIOD_UNLOCK: "danger",
	ENTRY_REVERSED: "warning",
	ENTRY_MANUAL: "neutral",
	EXPORT_RUN: "neutral",
	INVOICE_ISSUED: "success",
	INVOICE_PAID_MANUAL: "info",
	CREDIT_NOTE_ISSUED: "terra",
	VAT_SUBMITTED: "success",
};
const SEV_CLASS: Record<string, TagClass> = {
	info: "info",
	warn: "warning",
	danger: "danger",
};

/** FIN-11 — Audit comptable & contrôles (audit-trail + anomalies). */
export function AdminAuditPage() {
	const { data: log } = useAuditLog();
	const { data: anomalies } = useAnomalies();

	return (
		<>
			<PageHead
				title="Audit & contrôles"
				sub="Audit-trail immuable (rétention 10 ans) · détection d'anomalies"
			/>

			<Card>
				<CardHead
					title="Anomalies détectées"
					right={
						<Tag
							variant={(anomalies?.length ?? 0) === 0 ? "success" : "warning"}
							noDot
						>
							{anomalies?.length ?? 0} anomalie(s)
						</Tag>
					}
				/>
				<CardBody>
					{(anomalies?.length ?? 0) === 0 ? (
						<div className="muted" style={{ padding: 8 }}>
							Aucune anomalie détectée (montants ronds, hors période,
							déséquilibres).
						</div>
					) : (
						<div style={{ display: "grid", gap: 8 }}>
							{(anomalies ?? []).map((a, i) => (
								<div
									key={i}
									style={{ display: "flex", gap: 10, alignItems: "center" }}
								>
									<Tag variant={SEV_CLASS[a.severity] ?? "neutral"}>
										{a.type}
									</Tag>
									<span>{a.message}</span>
									{a.reference && (
										<span className="mono muted" style={{ fontSize: 11 }}>
											{a.reference}
										</span>
									)}
								</div>
							))}
						</div>
					)}
				</CardBody>
			</Card>

			<Card style={{ marginTop: 16 }}>
				<CardHead
					title="Journal d'audit"
					sub="Actions sensibles : lock, unlock, contrepassation, export, avoir…"
				/>
				<CardBody>
					<div
						className="table-wrap"
						style={{ margin: 0, maxHeight: 520, overflow: "auto" }}
					>
						<table className="table">
							<thead>
								<tr>
									<th>Date</th>
									<th>Acteur</th>
									<th>Action</th>
									<th>Cible</th>
									<th>Détail</th>
								</tr>
							</thead>
							<tbody>
								{(log?.length ?? 0) === 0 && (
									<tr>
										<td
											colSpan={5}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Aucune entrée d'audit.
										</td>
									</tr>
								)}
								{(log ?? []).map((e) => (
									<tr key={e.id}>
										<td className="muted" style={{ fontSize: 12 }}>
											{new Date(e.occurredAt).toLocaleString("fr-FR")}
										</td>
										<td style={{ fontSize: 12 }}>{e.actorLabel ?? "—"}</td>
										<td>
											<Tag variant={ACTION_CLASS[e.action] ?? "neutral"} noDot>
												{e.action}
											</Tag>
										</td>
										<td className="mono" style={{ fontSize: 11 }}>
											{e.targetType
												? `${e.targetType}:${e.targetId?.slice(0, 8) ?? ""}`
												: "—"}
										</td>
										<td
											className="muted"
											style={{
												fontSize: 11,
												maxWidth: 320,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{e.payload ? JSON.stringify(e.payload) : ""}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardBody>
			</Card>
		</>
	);
}
